import React, { Suspense, lazy, useState, useEffect } from 'react';
import { Switch, Route } from 'wouter';
import { queryClient } from './lib/queryClient';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AuthProvider } from '@/lib/AuthProvider';
import { LegislativeProvider } from '@/lib/LegislativeContext';
import { SidebarProvider } from '@/lib/SidebarProvider';
import { NotificationProvider } from '@/lib/NotificationProvider';
import { ThemeProvider } from '@/lib/ThemeProvider';
import ScrollToTop from '@/components/ScrollToTop';
import { AccountProvider } from '@/lib/AccountProvider';
import { PortalGuard } from '@/components/PortalGuard';
import { TimestampProvider } from '@/lib/TimestampProvider';
import { LoadingState } from '@/components/ui/loading-state';
import landing from './pages/landing';
import { isPortalSubdomain } from '@/hooks/use-portal-subdomain';
import { OnboardingFlowModal } from '@/components/modals/onboarding-flow-modal';
import { useAuth } from '@/hooks/use-auth';
import { TourProvider } from '@/lib/TourProvider';

// Core application pages
const Dashboard = lazy(() => import('@/pages/dashboard'));
const Reports = lazy(() => import('@/pages/reports'));
const AiReport = lazy(() => import('@/pages/ai-report'));
const Members = lazy(() => import('@/pages/members'));
const MemberContacts = lazy(() => import('@/pages/member-contacts'));
const GuestMembers = lazy(() => import('@/pages/guest-members'));
const GuestLogin = lazy(() => import('@/pages/guest-login'));
const AcceptInvite = lazy(() => import('@/pages/accept-invite'));
const GuestDashboard = lazy(() => import('@/pages/guest-dashboard'));
const GuestTrackBills = lazy(() => import('@/pages/guest-track-bills'));
const GuestTrackedActivity = lazy(() => import('@/pages/guest-tracked-activity'));
const TrackBills = lazy(() => import('@/pages/track-bills'));
const GuestSharedTags = lazy(() => import('@/pages/guest-shared-tags'));
const GuestSharedRelationships = lazy(() => import('@/pages/guest-shared-relationships'));
const GuestTasks = lazy(() => import('@/pages/guest-tasks'));
const GuestUploadTrackList = lazy(() => import('@/pages/guest-upload-track-list'));
const UploadTrackList = lazy(() => import('@/pages/upload-track-list'));
const About = lazy(() => import('@/pages/about'));
const DemoSignup = lazy(() => import('@/pages/demo-signup'));
const InviteFriends = lazy(() => import('@/pages/invite-friends'));
const MemberDetails = lazy(() => import('@/pages/member-details'));
const BillDetails = lazy(() => import('@/pages/bill-details'));
const Bills = lazy(() => import('@/pages/bills'));
const SubjectBills = lazy(() => import('@/pages/subject-bills'));
const StatuteBills = lazy(() => import('@/pages/statute-bills'));
const StatutesPage = lazy(() => import('@/pages/statutes'));
const RegsPage = lazy(() => import('@/pages/regs'));
const RegDetailsPage = lazy(() => import('@/pages/reg-details'));
const Committees = lazy(() => import('@/pages/committees'));
const CommitteeDetails = lazy(() => import('@/pages/committee-details'));
const Tracks = lazy(() => import('@/pages/tracks'));
const UserSettings = lazy(() => import('@/pages/user-settings'));
const PortalSettings = lazy(() => import('@/pages/portal-settings'));
const Storage = lazy(() => import('@/pages/storage'));
const AuthPage = lazy(() => import('@/pages/auth-page'));
const NotFound = lazy(() => import('@/pages/not-found'));
const CompanyTickSheetPage = lazy(() => import('@/pages/company-tick-sheet'));
const MemberTickSheetPage = lazy(() => import('@/pages/member-tick-sheet'));
const LobbySearch = lazy(() => import('@/pages/lobby'));
const LobbyistDetails = lazy(() => import('@/pages/lobby/lobbyist-details'));
const CompanyDetails = lazy(() => import('@/pages/lobby/company-details'));
const FirmDetails = lazy(() => import('@/pages/lobby/firm-details'));
const CampaignFinance = lazy(() => import('@/pages/campaign-finance'));
const BillingPage = lazy(() => import('@/pages/billing'));

// Analyst Management Portal
const AnalystPortal = lazy(() => import('@/pages/analyst-portal'));
const AnalystDetails = lazy(() => import('@/pages/analysts/[id]'));
const TaskDetails = lazy(() => import('@/pages/tasks/[id]'));
const NewTask = lazy(() => import('@/pages/tasks/new'));
const TeamDetails = lazy(() => import('@/pages/teams/[id]'));

// Tools
const LobbyistDashboard = lazy(() => import('@/pages/tools/lobby/dashboard'));
const LobbyistsPage = lazy(() => import('@/pages/tools/lobby/lobbyists'));
const AdvocateManagementDetails = lazy(
  () => import('@/pages/tools/lobby/advocate-management-details'),
);
const InviteLobbyistsPage = lazy(() => import('@/pages/tools/lobby/invite'));
const ReportsPage = lazy(() => import('@/pages/tools/lobby/reports'));
const Relationships = lazy(() => import('@/pages/tools/lobby/relationships'));
const TestimoniesPage = lazy(() => import('@/pages/tools/lobby/testimonies'));
const TestimonyPage = lazy(() => import('@/pages/tools/lobby/testimonies/[id]'));
const TrackedBillsPage = lazy(() => import('@/pages/tools/lobby/tracked-bills'));
const BillTagsPage = lazy(() => import('@/pages/tools/lobby/bill-tags'));
const UploadTrackListPage = lazy(() => import('@/pages/tools/lobby/upload-track-list'));
const ClientCRM = lazy(() => import('@/pages/tools/lobby/client-crm'));
const ClientCRMDetails = lazy(() => import('@/pages/tools/lobby/client-crm-details'));
const AutoTrackedBillsPage = lazy(() => import('@/pages/auto-tracked-bills'));
const AutoTrackedBillDetails = lazy(() => import('@/pages/auto-tracked-bill-details'));
const TrackedBillActivityPage = lazy(() => import('@/pages/tracked-bill-activity'));

// Committee Videos
const CommitteeVideos = lazy(() => import('@/pages/committee-videos'));
const VideoDetail = lazy(() => import('@/pages/committee-videos/[id]'));
const CreateClip = lazy(() => import('@/pages/committee-videos/create-clip'));
const CreateSummary = lazy(() => import('@/pages/committee-videos/create-summary'));
const ShareVideo = lazy(() => import('@/pages/committee-videos/share'));

// Search and analysis
const BillReport = lazy(() => import('@/pages/advanced-search'));
const VoteReport = lazy(() => import('@/pages/vote-report'));
const SearchResults = lazy(() => import('@/pages/search-results'));
const CalendarPage = lazy(() => import('@/pages/calendar'));
const BillCompare = lazy(() => import('@/pages/bill-compare'));
const NotesPage = lazy(() => import('@/pages/notes'));
const MeetingsPage = lazy(() => import('@/pages/meetings'));
const Accounts = lazy(() => import('./pages/accounts'));
const LiveActionsListPage = lazy(() => import('@/pages/live-actions'));

// Portal pages
const PortalHome = lazy(() => import('@/pages/portal-home'));
const PortalAuth = lazy(() => import('@/pages/portal-auth'));
const PortalBillDetails = lazy(() => import('@/pages/portal-bill-details'));
const PortalClipsPage = lazy(() => import('@/pages/portal-clips'));
const PortalCommitteeSummariesPage = lazy(() => import('@/pages/portal-committee-summaries'));
const PortalCommitteeActivityPage = lazy(
  () => import('@/pages/portal-committee-activity'),
);
const PortalPage = lazy(() => import('@/pages/portal-page'));

// Marketing pages
const FeaturesPage = lazy(() => import('./components/splash-screen/FeaturesPage'));
const AboutPage = lazy(() => import('@/components/splash-screen/AboutPage'));
const LiveActionPage = lazy(() => import('@/components/splash-screen/LiveActionsPage'));
const AdvocateManagementPage = lazy(
  () => import('@/components/splash-screen/AdvocateManagementPage'),
);
const NightWriterPage = lazy(() => import('./components/splash-screen/NightWriterPage'));
const AnalystManagementPage = lazy(
  () => import('./components/splash-screen/AnalystManagementPage'),
);
const AiReportsPage = lazy(() => import('./components/splash-screen/AiReportsPage'));
const TextAlertsPage = lazy(() => import('./components/splash-screen/TextAlertsPage'));
const SearchPage = lazy(() => import('./components/splash-screen/SearchPage'));
const BillAgentPage = lazy(() => import('./components/splash-screen/BillAgentPage'));
const CommitteeAgentPage = lazy(() => import('./components/splash-screen/CommitteeAgentPage'));
const AutoTrackPage = lazy(() => import('./components/splash-screen/AutoTrackPage'));
const BillComparisonPage = lazy(() => import('./components/splash-screen/BillComparisonPage'));
const BillsPortalPage = lazy(() => import('./components/splash-screen/BillsPortalPage'));
const MembersPortalPage = lazy(() => import('./components/splash-screen/MembersPortalPage'));
const CommitteesPortalPage = lazy(() => import('./components/splash-screen/CommitteesPortalPage'));
const TranscriptHubPage = lazy(() => import('./components/splash-screen/TranscriptHubPage'));
const StatutesPortalPage = lazy(() => import('./components/splash-screen/StatutesPortalPage'));
const CampaignFinancePage = lazy(() => import('./components/splash-screen/CampaignFinancePage'));
const LobbyPortalPage = lazy(() => import('./components/splash-screen/LobbyPortalPage'));
const TrackPage = lazy(() => import('./components/splash-screen/TrackPage'));
const ReportPage = lazy(() => import('./components/splash-screen/ReportPage'));
const HelpPage = lazy(() => import('./components/splash-screen/HelpPage'));
const TermsPage = lazy(() => import('./components/splash-screen/TermsPage'));
const BlogPostPage = lazy(() => import('./components/splash-screen/BlogPostPage'));
const CareersPage = lazy(() => import('./components/splash-screen/CareersPage'));
const Careers2Page = lazy(() => import('./components/splash-screen/Careers2Page'));
const PromotionCheckoutPage = lazy(() => import('@/pages/promotion-checkout'));
const PromotionCheckoutSuccessPage = lazy(() => import('@/pages/promotion-checkout-success'));
const WhyLegisIQPage = lazy(() => import('./components/splash-screen/WhyLegisIQPage'));
const LobbyistsSolutionsPage = lazy(
  () => import('./components/splash-screen/LobbyistsSolutionsPage'),
);
const AssociationsSolutionsPage = lazy(
  () => import('./components/splash-screen/AssociationsSolutionsPage'),
);
const CorporationsSolutionsPage = lazy(
  () => import('./components/splash-screen/CorporationsSolutionsPage'),
);
const BlogHome = lazy(() => import('./pages/blogs'));
const Blog1 = lazy(() => import('./pages/blogs/blogPost1'));
const Blog2 = lazy(() => import('./pages/blogs/blogPost2'));
const Blog3 = lazy(() => import('./pages/blogs/blogPost3'));
const Blog4 = lazy(() => import('./pages/blogs/blogPost4'));

const ChatWidget = lazy(() => import('@/components/chat/ChatWidget'));

// Agent pages
const CommitteeAgentDashboard = lazy(() => import('@/pages/agents/committee-agent'));
const BillAgentDashboard = lazy(() => import('@/pages/agents/bill-agent'));

const routerFallback = <LoadingState fullScreen message="Loading experience..." />;

function NewUserWelcome() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    // Show modal if user is authenticated and is a new user
    if (isAuthenticated && !isLoading && user?.NewUser) {
      setShowModal(true);
    } else if (isAuthenticated && !isLoading && !user?.NewUser) {
      // User is no longer new, ensure modal is closed
      setShowModal(false);
    }
  }, [isAuthenticated, isLoading, user?.NewUser]);

  const handleClose = () => {
    // Only close if the user is no longer marked as new (API call succeeded)
    if (!user?.NewUser) {
      setShowModal(false);
    }
  };

  return <OnboardingFlowModal isOpen={showModal} onClose={handleClose} />;
}

function Router() {
  // Check if we're on a portal subdomain
  const onPortalSubdomain = isPortalSubdomain();

  return (
    <>
      <ScrollToTop />
      <NewUserWelcome />
      <Suspense fallback={routerFallback}>
        <Switch>
          {/* Portal Subdomain Routes - these take priority when on a portal subdomain */}
          {onPortalSubdomain ? (
            <>
              <Route path="/clips" component={PortalClipsPage} />
              <Route path="/committee-activity" component={PortalCommitteeActivityPage} />
              <Route path="/committee-summaries" component={PortalCommitteeSummariesPage} />
              <Route path="/" component={PortalHome} />
              <Route path="/auth" component={PortalAuth} />
              <Route path="/bills/:id" component={PortalBillDetails} />
              <Route path="/pages/:slug" component={PortalPage} />
            </>
          ) : (
            <>
              {/* Splash Page */}
              <Route path="/" component={landing} />
              <Route path="/why-legisiq" component={WhyLegisIQPage} />
              <Route path="/features" component={FeaturesPage} />
              <Route path="/about" component={AboutPage} />
              <Route path="/solutions/lobbyists" component={LobbyistsSolutionsPage} />
              <Route path="/solutions/associations" component={AssociationsSolutionsPage} />
              <Route path="/solutions/corporations" component={CorporationsSolutionsPage} />
              <Route path="/live-actions" component={LiveActionPage} />
              <Route path="/advocate-management" component={AdvocateManagementPage} />
              <Route path="/automated-reports" component={NightWriterPage} />
              <Route path="/analyst-management" component={AnalystManagementPage} />
              <Route path="/ai-reports" component={AiReportsPage} />
              <Route path="/file-storage" component={TextAlertsPage} />
              <Route path="/search" component={SearchPage} />
              <Route path="/products/bill-agent" component={BillAgentPage} />
              <Route path="/products/committee-agent" component={CommitteeAgentPage} />
              <Route path="/products/auto-track" component={AutoTrackPage} />
              <Route path="/products/bill-comparison" component={BillComparisonPage} />
              <Route path="/portals/bills" component={BillsPortalPage} />
              <Route path="/portals/members" component={MembersPortalPage} />
              <Route path="/portals/committees" component={CommitteesPortalPage} />
              <Route path="/portals/transcript-hub" component={TranscriptHubPage} />
              <Route path="/portals/statutes" component={StatutesPortalPage} />
              <Route path="/portals/campaign-finance" component={CampaignFinancePage} />
              <Route path="/portals/lobby" component={LobbyPortalPage} />
              <Route path="/track" component={TrackPage} />
              <Route path="/report" component={ReportPage} />
              <Route path="/help" component={HelpPage} />
              <Route path="/termsandprivacy" component={TermsPage} />
              <Route path="/blog" component={BlogHome} />
              <Route path="/blog-post" component={BlogPostPage} />
              <Route path="/blog/post/1" component={Blog1} />
              <Route path="/blog/post/2" component={Blog2} />
              <Route path="/blog/post/3" component={Blog3} />
              <Route path="/blog/post/4" component={Blog4} />
              <Route path="/careers" component={CareersPage} />
              <Route path="/careers-2" component={Careers2Page} />
              <Route path="/promotion/checkout" component={PromotionCheckoutPage} />
              <Route path="/promotion/checkout-success" component={PromotionCheckoutSuccessPage} />
              {/* Main App */}
              <Route path="/accept-invite" component={AcceptInvite} />
              <Route path="/guest-login" component={GuestLogin} />
              <Route path="/guest-dashboard" component={GuestDashboard} />
              <Route path="/guest-members" component={GuestMembers} />
              <Route path="/guest-track-bills" component={GuestTrackBills} />
              <Route path="/guest-tracked-activity" component={GuestTrackedActivity} />
              <Route path="/guest-shared-tags" component={GuestSharedTags} />
              <Route path="/guest-shared-relationships" component={GuestSharedRelationships} />
              <Route path="/guest-tasks" component={GuestTasks} />
              <Route path="/guest-upload-track-list" component={GuestUploadTrackList} />

              <Route path="/auth" component={AuthPage} />
              <Route path="/portal-auth" component={PortalAuth} />
              <Route path="/dashboard" component={Dashboard} />
              <Route path="/calendar" component={CalendarPage} />
              <Route path="/dashboard/reports" component={Reports} />
              <Route path="/dashboard/reports/bill" component={BillReport} />
              <Route path="/dashboard/reports/votes" component={VoteReport} />
              <Route path="/accounts" component={Accounts} />
              <Route path="/billing" component={BillingPage} />
              <Route path="/dashboard/reports/ai" component={AiReport} />
              <Route path="/members" component={Members} />
              <Route path="/member-contacts" component={MemberContacts} />
              <Route path="/members/:id" component={MemberDetails} />
              <Route path="/bills" component={Bills} />
              <Route path="/bills/:id" component={BillDetails} />
              <Route path="/bills/:billId/company-tick-sheet" component={CompanyTickSheetPage} />
              <Route path="/bills/:billId/member-tick-sheet" component={MemberTickSheetPage} />
              <Route path="/subjects" component={SubjectBills} />
              <Route path="/subjects/:subjectCode" component={SubjectBills} />
              <Route path="/statutes" component={StatutesPage} />
              <Route path="/statutes/:id" component={StatuteBills} />
              <Route path="/regs" component={RegsPage} />
              <Route path="/regs/:id" component={RegDetailsPage} />
              <Route path="/committees" component={Committees} />
              <Route path="/committees/:id" component={CommitteeDetails} />
              <Route path="/lobby" component={LobbySearch} />
              <Route path="/lobby/lobbyist/:id" component={LobbyistDetails} />
              <Route path="/lobby/firm/:id" component={FirmDetails} />
              <Route path="/lobby/company/:id" component={CompanyDetails} />
              <Route path="/campaign-finance" component={CampaignFinance} />
              <Route path="/tracks" component={Tracks} />
              <Route path="/track-bills" component={TrackBills} />
              <Route path="/upload-track-list" component={UploadTrackList} />
              <Route path="/user-settings" component={UserSettings} />
              <Route path="/portal-settings" component={PortalSettings} />
              <Route path="/agentic-track-bills/:billId" component={AutoTrackedBillDetails} />
              <Route path="/agentic-track-bills" component={AutoTrackedBillsPage} />
              <Route path="/dashboard/recent-activity" component={TrackedBillActivityPage} />
              <Route path="/dashboard/live-actions" component={LiveActionsListPage} />
              <Route path="/storage" component={Storage} />
              <Route path="/storage/:folder*" component={Storage} />
              <Route path="/notes" component={NotesPage} />
              <Route path="/meetings" component={MeetingsPage} />

              {/* Agent Dashboard Routes */}
              <Route path="/agents/committee-agent" component={CommitteeAgentDashboard} />
              <Route path="/agents/bill-agent" component={BillAgentDashboard} />

              {/* Analyst Management Portal Routes */}
              <Route
                path="/analyst-portal"
                component={() => (
                  <PortalGuard portal="analystPortal">
                    <AnalystPortal />
                  </PortalGuard>
                )}
              />
              <Route
                path="/analysts/:id"
                component={() => (
                  <PortalGuard portal="analystPortal">
                    <AnalystDetails />
                  </PortalGuard>
                )}
              />
              <Route
                path="/teams/:id"
                component={() => (
                  <PortalGuard portal="analystPortal">
                    <TeamDetails />
                  </PortalGuard>
                )}
              />
              <Route
                path="/tasks/new"
                component={() => (
                  <PortalGuard portal="analystPortal">
                    <NewTask />
                  </PortalGuard>
                )}
              />
              <Route
                path="/tasks/:id"
                component={() => (
                  <PortalGuard portal="analystPortal">
                    <TaskDetails />
                  </PortalGuard>
                )}
              />

              <Route
                path="/tools/lobby/dashboard"
                component={() => (
                  <PortalGuard portal="lobbyistPortal">
                    <LobbyistDashboard />
                  </PortalGuard>
                )}
              />
              <Route
                path="/tools/lobby/lobbyists"
                component={() => (
                  <PortalGuard portal="lobbyistPortal">
                    <LobbyistsPage />
                  </PortalGuard>
                )}
              />
              <Route
                path="/tools/lobby/tracked-bills"
                component={() => (
                  <PortalGuard portal="lobbyistPortal">
                    <TrackedBillsPage />
                  </PortalGuard>
                )}
              />
              <Route
                path="/tools/lobby/client-crm"
                component={() => (
                  <PortalGuard portal="clientCrm">
                    <ClientCRM />
                  </PortalGuard>
                )}
              />
              <Route
                path="/tools/lobby/client-crm/:companyId"
                component={() => (
                  <PortalGuard portal="clientCrm">
                    <ClientCRMDetails />
                  </PortalGuard>
                )}
              />
              <Route
                path="/tools/lobby/bill-tags"
                component={() => (
                  <PortalGuard portal="lobbyistPortal">
                    <BillTagsPage />
                  </PortalGuard>
                )}
              />
              <Route
                path="/tools/lobby/upload-track-list"
                component={() => (
                  <PortalGuard portal="lobbyistPortal">
                    <UploadTrackListPage />
                  </PortalGuard>
                )}
              />
              <Route
                path="/tools/lobby/lobbyist/:id"
                component={() => (
                  <PortalGuard portal="lobbyistPortal">
                    <AdvocateManagementDetails />
                  </PortalGuard>
                )}
              />
              <Route
                path="/tools/lobby/invite"
                component={() => (
                  <PortalGuard portal="lobbyistPortal">
                    <InviteLobbyistsPage />
                  </PortalGuard>
                )}
              />
              <Route
                path="/tools/lobby/reports"
                component={() => (
                  <PortalGuard portal="lobbyistPortal">
                    <ReportsPage />
                  </PortalGuard>
                )}
              />
              <Route
                path="/tools/lobby/relationships"
                component={() => (
                  <PortalGuard portal="lobbyistPortal">
                    <Relationships />
                  </PortalGuard>
                )}
              />
              <Route
                path="/tools/lobby/testimonies"
                component={() => (
                  <PortalGuard portal="lobbyistPortal">
                    <TestimoniesPage />
                  </PortalGuard>
                )}
              />
              <Route
                path="/tools/lobby/testimonies/:id"
                component={() => (
                  <PortalGuard portal="lobbyistPortal">
                    <TestimonyPage />
                  </PortalGuard>
                )}
              />

              {/* Committe Video */}
              <Route path="/tools/committee-videos" component={CommitteeVideos} />
              <Route path="/tools/committee-videos/:id" component={VideoDetail} />
              <Route path="/tools/video/create-clip/:id" component={CreateClip} />
              <Route path="/tools/video/create-summary/:id" component={CreateSummary} />
              <Route path=":stateCode/videos/share/:clipId" component={ShareVideo} />

              <Route path="/search-results" component={SearchResults} />

              <Route path="/about" component={About} />
              <Route path="/demo-signup" component={DemoSignup} />
              <Route path="/invite-friends" component={InviteFriends} />

              {/* Bill Compare */}
              <Route path="/bill/compare" component={BillCompare} />
            </>
          )}

          {/* 404 - Must be last, outside conditional */}
          <Route path="*" component={NotFound} />
        </Switch>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <AccountProvider>
            <NotificationProvider>
              <LegislativeProvider>
                <SidebarProvider>
                  <TooltipProvider>
                    <TimestampProvider>
                      <TourProvider>
                        <Toaster />
                        <Suspense fallback={null}>
                          <ChatWidget />
                        </Suspense>
                        <Router />
                      </TourProvider>
                    </TimestampProvider>
                  </TooltipProvider>
                </SidebarProvider>
              </LegislativeProvider>
            </NotificationProvider>
          </AccountProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
